import { type } from 'arktype';
import { dayKeySchema } from './day-key.model';
import { narrowError } from './schema-narrow';
import { utcInstantSchema } from './utc-instant.model';

export const BLOCKED_WINDOW_SCOPE_TYPES = ['department', 'location', 'team', 'person', 'shift', 'shift_unit'] as const;

export type BlockedWindowScopeType = (typeof BLOCKED_WINDOW_SCOPE_TYPES)[number];

export const BLOCKED_WINDOW_OVERRIDE_MODES = ['lift', 'replace', 'add'] as const;

export type BlockedWindowOverrideMode = (typeof BLOCKED_WINDOW_OVERRIDE_MODES)[number];

export const BLOCKED_WINDOW_SEVERITIES = ['warn', 'block'] as const;

export type BlockedWindowSeverity = (typeof BLOCKED_WINDOW_SEVERITIES)[number];

export const DAILY_REST_WINDOW_KEY = 'daily_rest';

export const BLOCKED_WINDOW_EXCEPTION_SUBJECT_TYPE = 'timetracking.blocked_window_exception';

export interface BlockedWindowSource {
  level: 'global' | BlockedWindowScopeType;
  scopeId: string | null;
  scopeLabel: string | null;
  entryId: string | null;
  reason: string | null;
  permitReference: string | null;
}

export interface ResolvedBlockedWindow {
  windowKey: string;
  fromTime: string;
  toTime: string;
  severity: BlockedWindowSeverity;
  legalReference: string | null;
  source: BlockedWindowSource;
  overlapMinutes?: number;
}

export type BlockedWindowVerdictEntry = ResolvedBlockedWindow & { overlapMinutes: number; excepted?: boolean; exceptionRequestId?: string };

export interface BlockedWindowRequestResult {
  requestId: string;
  windows: ResolvedBlockedWindow[];
}

export interface BookingBlockedWindowException {
  requestId: string;
  approvedByPersonId: string | null;
  approvedAt: string;
  startTime: string;
  endTime: string;
  windowKeys: string[];
}

export interface BlockedWindowExceptionPayload {
  personId: string;
  bookingId: string;
  date: string;
  startTime: string;
  endTime: string;
  windows: ResolvedBlockedWindow[];
}

export interface BlockedWindowExceptionDetailContext extends BlockedWindowExceptionPayload {
  bookingActive: boolean;
}

export interface EffectiveBlockedWindowsResponse {
  personId: string;
  date: string;
  windows: ResolvedBlockedWindow[];
}

export interface BlockedWindowOverride {
  id: string;
  scopeType: BlockedWindowScopeType;
  scopeId: string;
  scopeLabel: string | null;
  windowKey: string;
  mode: BlockedWindowOverrideMode;
  fromTime: string | null;
  toTime: string | null;
  weekdays: number[] | null;
  includeHolidays: boolean | null;
  severity: BlockedWindowSeverity | null;
  reason: string | null;
  permitReference: string | null;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
}

const blockedWindowSourceSchema = type({
  level: type.enumerated('global', ...BLOCKED_WINDOW_SCOPE_TYPES),
  scopeId: 'string | null',
  scopeLabel: 'string | null',
  entryId: 'string | null',
  reason: 'string | null',
  permitReference: 'string | null',
});

export const resolvedBlockedWindowSchema = type({
  windowKey: 'string > 0',
  fromTime: 'string',
  toTime: 'string',
  severity: type.enumerated(...BLOCKED_WINDOW_SEVERITIES),
  legalReference: 'string | null',
  source: blockedWindowSourceSchema,
  'overlapMinutes?': 'number',
});

export const blockedWindowRequestResultSchema = type({
  requestId: 'string > 0',
  windows: resolvedBlockedWindowSchema.array(),
});

export const blockedWindowExceptionPayloadSchema = type({
  personId: 'string > 0',
  bookingId: 'string > 0',
  date: dayKeySchema,
  startTime: utcInstantSchema,
  endTime: utcInstantSchema,
  windows: resolvedBlockedWindowSchema.array().atLeastLength(1),
});

export const timeOfDaySchema = type(/^([01]\d|2[0-3]):[0-5]\d$/).configure({ description: 'Local time of day, HH:mm.' });

export const endTimeOfDaySchema = type(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/).configure({
  description: 'Local time of day, HH:mm; 24:00 allowed.',
});

export const weekdaysSchema = type('(1 <= number.integer <= 7)[]')
  .narrow(
    narrowError((weekdays: number[]) =>
      weekdays.length > 0 && new Set(weekdays).size === weekdays.length ? null : 'unique ISO weekdays 1-7'
    )
  )
  .configure({ description: 'Unique ISO weekdays 1 (Monday) to 7 (Sunday) the window starts on.' });

type CreateBlockedWindowOverrideInput = {
  mode: BlockedWindowOverrideMode;
  reason?: string | null;
  fromTime?: string;
  toTime?: string;
  severity?: BlockedWindowSeverity;
  validFrom: string;
  validTo?: string | null;
};

function createOverrideError(input: CreateBlockedWindowOverrideInput): string | null {
  if (input.validTo != null && input.validTo < input.validFrom) return 'validTo on or after validFrom';
  if (input.mode === 'lift') return input.reason?.trim() ? null : 'a non-blank reason when mode is lift';
  return input.fromTime && input.toTime && input.severity ? null : 'fromTime, toTime and severity when mode is replace or add';
}

export const createBlockedWindowOverrideSchema = type({
  scopeType: type.enumerated(...BLOCKED_WINDOW_SCOPE_TYPES),
  scopeId: type('string > 0').configure({ description: 'Id of the department, location, team, person, shift or shift unit.' }),
  windowKey: type('string > 0').configure({ description: 'Key of the blocked window the entry lifts, replaces or adds.' }),
  mode: type.enumerated(...BLOCKED_WINDOW_OVERRIDE_MODES),
  'fromTime?': timeOfDaySchema,
  'toTime?': endTimeOfDaySchema,
  'weekdays?': weekdaysSchema,
  'includeHolidays?': 'boolean',
  'severity?': type.enumerated(...BLOCKED_WINDOW_SEVERITIES),
  'reason?': 'string | null',
  'permitReference?': 'string | null',
  validFrom: dayKeySchema,
  'validTo?': dayKeySchema.or('null'),
})
  .narrow(narrowError(createOverrideError))
  .configure({
    description: 'Create a blocked-window override; lift requires a reason, replace and add require fromTime, toTime and severity.',
  });

export type CreateBlockedWindowOverride = typeof createBlockedWindowOverrideSchema.t;

export const updateBlockedWindowOverrideSchema = type({
  validTo: dayKeySchema.configure({ description: 'Last day (inclusive) the override applies — ends the entry, never deletes it.' }),
}).configure({ description: 'End a blocked-window override at a key date.' });

export type UpdateBlockedWindowOverride = typeof updateBlockedWindowOverrideSchema.t;
